import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import axiosAPI, { get } from 'utils/axios';
import { convertBlobToBase64 } from 'utils';
import {
  FILEMANAGER_BATCH_DOWNLOAD,
  FILEMANAGER_BATCH_LIST,
  FILEMANAGER_ENDPOINT_FILE,
  FILEMANAGER_ENDPOINT_LIST,
} from 'config/endpoints';
import { FilesList } from 'types/filemanager.contract';
import { AssetCardProps } from 'components/AssetCard/types';
import { UseMutationCallbackFunction, VoidFunc } from './events/records/types';
import { GenericQueryOptions } from './events/types';

export interface IFileManagerProps {
  keyName: string;
  keyType: string;
  fileName?: string;
  file?: File;
  showAuditLog?: boolean;
}

export interface IUploadFileProps {
  keyName: string;
  keyType: string;
  file: File;
}

export const downloadFile = async ({
  keyName,
  keyType,
  fileName,
}: IFileManagerProps): Promise<any> => {
  const data = await axiosAPI.get<Blob>(
    `${FILEMANAGER_ENDPOINT_FILE}?key_name=${keyName}&key_type=${keyType}&file_name=${fileName}`,
    { responseType: 'blob' },
  );
  const base64File = await convertBlobToBase64(data.data);

  return base64File;
};

export const useDownloadFile = ({ keyName, keyType, fileName }: IFileManagerProps) => {
  return useQuery(
    ['downloadFile', keyName, keyType, fileName],
    () => downloadFile({ keyName, keyType, fileName }),
    { enabled: !!keyName && !!keyType && !!fileName },
  );
};

export const deleteFile = async ({
  keyName,
  keyType,
  fileName,
}: IFileManagerProps): Promise<any> => {
  await axiosAPI.delete(
    `${FILEMANAGER_ENDPOINT_FILE}?key_name=${keyName}&key_type=${keyType}&file_name=${fileName}`,
  );
};

// TODO: To be deprecated
export const listFiles = async ({
  keyName,
  keyType,
  showAuditLog,
}: {
  keyName: string;
  keyType: string;
  showAuditLog?: boolean;
}): Promise<any> => {
  const data = await axiosAPI.get(
    `${FILEMANAGER_ENDPOINT_LIST}?key_name=${keyName}&key_type=${keyType}&show_audit_log=${showAuditLog}`,
  );
  return data;
};

// TODO: To be deprecated
export const useListFiles = ({
  keyName,
  keyType,
  showAuditLog,
}: {
  keyName: string;
  keyType: string;
  showAuditLog?: boolean;
}) => {
  return useQuery(
    ['listFiles', keyName, keyType, showAuditLog],
    () => listFiles({ keyName, keyType, showAuditLog }),
    {
      enabled: !!keyName && !!keyType,
    },
  );
};

export const batchListFiles = async (batch_list: {
  batch_list: {
    key_name: string;
    key_type: string;
    show_audit_log?: boolean;
  }[];
}): Promise<any> => {
  const data = await axiosAPI.post(FILEMANAGER_BATCH_LIST, batch_list);
  return data;
};

export const useBatchListFilesQuery = ({
  batch_list,
  assets,
}: {
  batch_list: {
    key_name: string;
    key_type: string;
    show_audit_log?: boolean;
  }[];
  assets: AssetCardProps[];
}) => {
  return useQuery(
    ['BATCH_LIST_FILES', assets, 'PDF_LOADING'],
    () => batchListFiles({ batch_list }),
    {
      enabled: batch_list.length > 0,
    },
  );
};

export const batchDownloadFiles = async (batch_list: {
  batch_list: {
    file_name: string;
    key_name: string;
    key_type: string;
  }[];
}): Promise<any> => {
  const data = await axiosAPI.request({
    url: FILEMANAGER_BATCH_DOWNLOAD,
    data: batch_list,
    method: 'POST',
    responseType: 'arraybuffer',
  });
  return data;
};

export const useBatchDownloadFilesQuery = ({
  batch_list,
  queryKey,
}: {
  batch_list: {
    file_name: string;
    key_name: string;
    key_type: string;
  }[];
  queryKey: AssetCardProps[];
}) => {
  return useQuery(
    ['BATCH_DOWNLOAD_FILES', queryKey, 'PDF_LOADING'],
    () => batchDownloadFiles({ batch_list }),
    {
      enabled: queryKey.length > 0 && batch_list?.length > 0,
    },
  );
};

export const fetchFile = ({ keyName, keyType, fileName }: IFileManagerProps) =>
  get<Blob, unknown>(
    FILEMANAGER_ENDPOINT_FILE,
    { key_name: keyName, key_type: keyType, file_name: fileName },
    undefined,
    { responseType: 'blob' },
  );

export const useFilesListQuery = (
  { keyName, keyType, showAuditLog }: { keyName: string; keyType: string; showAuditLog?: boolean },
  queryOptions?: GenericQueryOptions<FilesList>,
) =>
  useQuery(
    ['FILES_LIST', keyName, keyType, showAuditLog],
    get<FilesList, unknown>(FILEMANAGER_ENDPOINT_LIST, {
      key_name: keyName,
      key_type: keyType,
      show_audit_log: showAuditLog,
    }),
    { ...queryOptions, enabled: !!keyName && !!keyType },
  );

export const useFileQuery = (
  { keyName, keyType, fileName }: IFileManagerProps,
  queryOptions?: Omit<UseQueryOptions<Blob, AxiosError, Blob>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(
    ['BLOB_FILE', 'PDF_LOADING', keyName, keyType, fileName],
    fetchFile({ keyName, keyType, fileName }),
    queryOptions,
  );
};

export const useFileDeleteMutation = (onSuccess?: UseMutationCallbackFunction<IFileManagerProps>) =>
  useMutation(
    (props: IFileManagerProps) => {
      return axiosAPI.delete(FILEMANAGER_ENDPOINT_FILE, {
        params: { key_name: props.keyName, key_type: props.keyType, file_name: props.fileName },
      });
    },
    {
      onSuccess,
      onError: (error: AxiosError) => {
        console.log(error);
      },
    },
  );

export const useFileUploadMutation = (
  onError: VoidFunc,
  onSuccess?: UseMutationCallbackFunction<{ formData: unknown; fileName?: string }>,
) =>
  useMutation(
    ({ formData }: { formData: unknown; fileName?: string }) =>
      axiosAPI.post<boolean>(FILEMANAGER_ENDPOINT_FILE, formData),
    {
      onError,
      onSuccess,
    },
  );
